import { INotificationMessage } from "@fl/shared/src/types/messages";
import {
  IDataExcel,
  IDataPDF,
  IExcelColumn,
  IFormFieldReport,
} from "@fl/shared/src/types/reports";
import { ITableDataColumns } from "@fl/shared/src/types/tables";
import moment from "moment";
import "moment/locale/pt-br";
import numeral from "numeral";
import "numeral/locales/pt-br";
import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Controller } from "react-hook-form";
import AsyncSelect from "react-select/async";

import SelectComp from "../../../components/select/selectComp";
import { useNotificationContext } from "../../../context/notifications";
import DefaultBasePage from "../../default/base";
import DefaultReportPage from "../../default/reports";

import ApiCondominios from "../../../services/suporte/auth/condominios";
import ApiFornecedores from "../../../services/suporte/auth/fornecedores";

import { FornecedoresListasAttributes } from "@fl/shared/src/types/models/suporte_fl/fornecedores_listas";
import ListaFornecedoresModal, {
  ListaFornecedoresResult,
} from "../../../components/modals/listaFornecedores";
import {
  FILTRAR_POR_DESPESAS,
  STATUS_DESPESAS,
} from "../../../components/select/defaultValuesSL";
import ApiFornecedoresListas from "../../../services/suporte/auth/fornecedoresListas";
import ApiReportSOS from "../../../services/suporte/auth/reports/sos";
import { formatCpfCnpj } from "../../../utils/stringsFormats";
import StringsFuncs from "../../../utils/stringsFuncs";

// ---------- tipos auxiliares ----------
type Option = { value: string; label: string };

// TIPAGEM usada na página
interface IFornecedorList {
  id_lista: number;
  nome_lista: string;
  observacao?: string | null;
  itens: Array<{
    id_item: number;
    id_lista: number;
    id_fornecedor: number;
    fornecedor?: {
      id_contato_con: number;
      st_nome_con?: string;
      st_cpf_con?: string;
      st_cnpj_con?: string;
    };
  }>;
}

// shape placeholder de resultado SOS (ajuste quando integrar com a API real)
interface ISosCompra {
  dt_despesa_des?: string; // "MM/DD/YYYY"
  dt_liquidacao_pdes?: string; // "MM/DD/YYYY"
  st_fantasia_cond?: string;
  st_nome_con?: string;
  st_documento_des?: string;
  vl_valorbruto_pdes?: number | string;
  vl_valor_pdes?: number | string;
  status_pag?: string; // "Aberto"/"Pago" etc.
}

// --------------------------------------

const RelatorioSOSComprasPrivatePage: React.FC = () => {
  const { addNotification } = useNotificationContext();

  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<INotificationMessage[]>([]);
  const [results, setResults] = useState<ISosCompra[]>([]);

  const [showListaFornecedoresModal, setShowListaFornecedoresModal] =
    useState(false);

  // cache id->option p/ manter labels dos selecionados
  const [fornCache, setFornCache] = useState<Record<string, Option>>({});
  // listas salvas
  const [fornecedorLists, setFornecedorLists] = useState<IFornecedorList[]>([]);
  const [selectedFornecedorLista, setSelectedFornecedorLista] =
    useState<FornecedoresListasAttributes | null>(null);

  // ids a aplicar no AsyncSelect após modal/seleção de lista
  const [forceSuppliersIds, setForceSuppliersIds] = useState<string[] | null>(
    null
  );

  // lista selecionada no combobox
  const [selectedListId, setSelectedListId] = useState<number | null>(null);

  // --------- utils: datas default = mês anterior ---------
  const prevMonthStart = moment()
    .subtract(1, "month")
    .startOf("month")
    .format("YYYY-MM-DD");
  const prevMonthEnd = moment()
    .subtract(1, "month")
    .endOf("month")
    .format("YYYY-MM-DD");

  // helper: aplica ids no cache + dispara aplicação no RHF via forceSuppliersIds
  const applyIdsToSelect = (idsNum: number[], source?: IFornecedorList) => {
    const ids = idsNum.map(String);

    // garante labels no fornCache
    setFornCache((prev) => {
      const next = { ...prev };
      idsNum.forEach((idNum) => {
        const id = String(idNum);
        if (!next[id]) {
          // tenta achar no source para label completo
          const sup = source?.itens?.find(
            (it) => it.id_fornecedor === idNum
          )?.fornecedor;
          const label = sup
            ? `[${new StringsFuncs(sup.id_contato_con).formatarNumeroMilhar(
                7
              )}] ${sup.st_nome_con ?? ""} - ${formatCpfCnpj(
                sup.st_cpf_con ?? sup.st_cnpj_con ?? ""
              )}`
            : `[${new StringsFuncs(idNum).formatarNumeroMilhar(
                7
              )}] Fornecedor ${id}`;
          next[id] = { value: id, label };
        }
      });
      return next;
    });

    // sinaliza para o campo custom aplicar no RHF
    setForceSuppliersIds(ids);
  };

  // limpar seleção de fornecedores (usado ao limpar/excluir lista)
  const clearSuppliersSelection = () => {
    // apenas dispara o "gatilho" de aplicação vazia; o setValue é feito dentro do custom field
    setForceSuppliersIds([]);
  };

  // --------- Condominios (multi, sem grupos) -------------
  const searchCondominiosByTerm = async (term: string) => {
    const result = await new ApiCondominios({ term }).find();
    if (result.success) {
      return (result.data || []).map((item: any) => ({
        value: String(item.id_condominio_cond),
        label: item.st_nome_cond,
      }));
    }
    return [];
  };

  const CondominiosComponent: IFormFieldReport = {
    fieldKey: "id_condominios",
    type: "custom",
    render: (methods) => {
      const { control, watch, setValue } = methods;
      const idCondominios = watch("id_condominios") || [];

      const allCondominios = () => {
        if (idCondominios.includes("-1")) {
          setValue("id_condominios", []);
        } else {
          setValue("id_condominios", ["-1"]);
        }
      };

      return (
        <Row>
          <Col>
            <Form.Label>
              Condomínios:{" "}
              <span className="link-button" onClick={allCondominios}>
                {idCondominios.includes("-1")
                  ? "Selecionar Condomínios"
                  : "Todos Condomínios"}
              </span>
            </Form.Label>

            <Controller
              name="id_condominios"
              control={control}
              defaultValue={["-1"]}
              render={({ field: { onChange, value } }) => {
                const [condOptions, setCondOptions] = useState<Option[]>([]);
                const selectedOptions = condOptions.filter((o) =>
                  (value || []).includes(o.value)
                );

                const handleChangeConds = (selected: any) => {
                  const newVals = selected
                    ? selected.map((s: any) => s.value)
                    : [];
                  // mescla no cache local p/ manter labels
                  const newly = (selected || []).map((s: any) => ({
                    value: s.value,
                    label: s.label,
                  }));
                  const merged = [
                    ...condOptions,
                    ...newly.filter(
                      (n: any) => !condOptions.some((o) => o.value === n.value)
                    ),
                  ];
                  setCondOptions(merged);
                  onChange(newVals);
                };

                const loadOptions = async (inputValue: string) => {
                  if ((value || []).includes("-1")) return []; // se "todos", não carrega nada
                  if (inputValue.trim().length < 2) return condOptions;
                  const fetched = await searchCondominiosByTerm(inputValue);
                  const merged = [
                    ...condOptions,
                    ...fetched.filter(
                      (n: any) => !condOptions.some((o) => o.value === n.value)
                    ),
                  ];
                  setCondOptions(merged);
                  return fetched;
                };

                return (
                  <AsyncSelect
                    isMulti
                    isDisabled={(value || []).includes("-1")}
                    value={selectedOptions}
                    defaultOptions={condOptions}
                    loadOptions={loadOptions}
                    onChange={handleChangeConds}
                    noOptionsMessage={() => "Sem resultados"}
                    placeholder={
                      (value || []).includes("-1")
                        ? "Todos Condomínios Selecionados"
                        : "Digite pelo menos 2 caracteres..."
                    }
                  />
                );
              }}
            />
          </Col>
        </Row>
      );
    },
    colSize: 12,
  };

  // --------- Fornecedores (avançado) + Listas -------------
  const searchFornecedoresByTerm = async (term: string) => {
    const result = await new ApiFornecedores({ term }).find();
    if (result.success) {
      const data = Array.isArray(result.data)
        ? result.data
        : Object.values(result.data || {});
      return data.map((item: any) => ({
        value: String(item.id_contato_con),
        label: `[${new StringsFuncs(item.id_contato_con).formatarNumeroMilhar(
          7
        )}] ${item.st_nome_con ?? ""} - ${formatCpfCnpj(
          item.st_cpf_con ?? item.st_cnpj_con ?? ""
        )}`,
      }));
    }
    return [];
  };

  // carrega listas e retorna as linhas (para uso após create/update/delete)
  const loadFornecedorLists = async (): Promise<IFornecedorList[]> => {
    const res = await new ApiFornecedoresListas().list();
    if (res.success) {
      const rows: IFornecedorList[] = Array.isArray(res.data)
        ? res.data
        : Object.values(res.data || {});
      setFornecedorLists(rows);

      // pré-carrega labels a partir de itens.fornecedor
      const next = { ...fornCache };
      rows.forEach((lst) => {
        lst.itens?.forEach((it) => {
          const f = it.fornecedor;
          if (!f) return;
          const id = String(f.id_contato_con);
          if (!next[id]) {
            next[id] = {
              value: id,
              label: `[${new StringsFuncs(
                f.id_contato_con
              ).formatarNumeroMilhar(
                7
              )}] ${f.st_nome_con ?? ""} - ${formatCpfCnpj(
                f.st_cpf_con ?? f.st_cnpj_con ?? ""
              )}`,
            };
          }
        });
      });
      setFornCache(next);
      return rows;
    }
    return [];
  };

  useEffect(() => {
    loadFornecedorLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const FornecedoresAvancadoComponent: IFormFieldReport = {
    fieldKey: "id_fornecedores",
    type: "custom",
    render: (methods) => {
      const { control, watch, setValue } = methods;
      const ids: string[] = watch("id_fornecedores") || [];

      // aplica seleção que veio do modal/seleção de lista
      useEffect(() => {
        if (forceSuppliersIds !== null) {
          setValue("id_fornecedores", forceSuppliersIds);
          // limpa o “gatilho” após aplicar
          setForceSuppliersIds(null);
        }
      }, [forceSuppliersIds, setValue]);

      const selectedOptions = ids
        .map((id) => fornCache[id])
        .filter(Boolean) as Option[];

      const handleChange = (selected: any) => {
        const opts: Option[] = selected || [];
        setFornCache((prev) => {
          const next = { ...prev };
          opts.forEach((o) => (next[o.value] = o));
          return next;
        });
        setValue(
          "id_fornecedores",
          opts.map((o) => o.value)
        );
      };

      const loadOptions = async (inputValue: string) => {
        const term = inputValue.trim();
        if (term.length < 2) return [];
        const fetched = await searchFornecedoresByTerm(term);
        setFornCache((prev) => {
          const next = { ...prev };
          fetched.forEach((o) => {
            if (!next[o.value]) next[o.value] = o;
          });
          return next;
        });
        return fetched;
      };

      // listas salvas (corrigido)
      const listOptions: Option[] = fornecedorLists.map((l) => ({
        value: String(l.id_lista),
        label: `${l.nome_lista} (${l.itens?.length || 0})`,
      }));

      const openCreateOrUpdateModal = () => {
        // monta payload para o modal com base no que está selecionado no select
        const newList: FornecedoresListasAttributes = {
          id_lista: selectedListId ?? undefined,
          nome_lista: selectedListId
            ? (fornecedorLists.find((l) => l.id_lista === selectedListId)
                ?.nome_lista ?? "")
            : "",
          observacao: selectedListId
            ? (fornecedorLists.find((l) => l.id_lista === selectedListId)
                ?.observacao ?? null)
            : "",
          itens: ids.map((id) => ({ id_fornecedor: Number(id) })),
        };
        setSelectedFornecedorLista(newList);
        setShowListaFornecedoresModal(true);
      };

      return (
        <>
          <Row className="g-2">
            <Col md={8}>
              <Form.Label>Fornecedores (buscar ≥ 2 caracteres):</Form.Label>
              <Controller
                name="id_fornecedores"
                control={control}
                defaultValue={[]}
                render={() => (
                  <AsyncSelect
                    isMulti
                    cacheOptions
                    defaultOptions={selectedOptions}
                    value={selectedOptions}
                    loadOptions={loadOptions}
                    onChange={handleChange}
                    noOptionsMessage={() => "Sem resultados"}
                    placeholder="Digite pelo menos 2 caracteres..."
                  />
                )}
              />
            </Col>

            <Col md={4}>
              <Form.Label>Listas Salvas:</Form.Label>
              <SelectComp
                options={listOptions}
                value={
                  listOptions.find((o) => o.value === String(selectedListId)) ||
                  null
                }
                onChange={async (opt: any) => {
                  const id = opt ? Number(opt.value) : null;
                  setSelectedListId(id);

                  if (id) {
                    // aplicando lista selecionada => injeta ids no AsyncSelect
                    const lst = fornecedorLists.find((l) => l.id_lista === id);
                    const idsNum = (lst?.itens ?? []).map(
                      (i) => i.id_fornecedor
                    );
                    applyIdsToSelect(idsNum, lst); // também preenche labels no cache
                  } else {
                    // limpou o Select de listas => também limpa fornecedores
                    clearSuppliersSelection(); // ou: applyIdsToSelect([]);
                  }
                }}
                isClearable
                placeholder="Selecione uma lista"
              />

              <div className="d-flex gap-2 mt-2">
                <Button
                  size="sm"
                  variant={
                    selectedListId ? "outline-primary" : "outline-success"
                  }
                  onClick={openCreateOrUpdateModal}
                  disabled={ids.length === 0 && !selectedListId}
                  title={
                    selectedListId ? "Atualizar Lista" : "Salvar como Lista"
                  }
                >
                  {selectedListId ? "Atualizar Lista" : "Salvar como Lista"}
                </Button>

                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={async () => {
                    if (!selectedListId) return;
                    setLoading(true);
                    try {
                      const res = await new ApiFornecedoresListas().delete(
                        selectedListId
                      );
                      if (res.success) {
                        if (res.message) addNotification(res.message[0]);
                        // limpa seleção de lista e fornecedores
                        setSelectedListId(null);
                        setSelectedFornecedorLista(null);
                        clearSuppliersSelection();
                        await loadFornecedorLists();
                      } else if (res.message) {
                        setMessages(res.message);
                      }
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={!selectedListId}
                >
                  Excluir Lista
                </Button>
              </div>
            </Col>
          </Row>
        </>
      );
    },
    colSize: 12,
  };

  // --------- fields (sem Formas/Remessas/Grupos) ----------
  const fields: IFormFieldReport[] = [
    {
      fieldKey: "dt_inicio",
      label: "Data Inicial",
      type: "date",
      validation: { required: "Data inicial é obrigatória" },
      colSize: 3,
    },
    {
      fieldKey: "dt_fim",
      label: "Data Final",
      type: "date",
      validation: { required: "Data final é obrigatória" },
      colSize: 3,
    },
    {
      fieldKey: "status",
      label: "Status dos Pagamentos",
      type: "select",
      placeholder: "Selecione...",
      options: STATUS_DESPESAS,
      colSize: 3,
    },
    {
      fieldKey: "despesas",
      label: "Filtrar Por",
      type: "select",
      placeholder: "Selecione...",
      options: FILTRAR_POR_DESPESAS,
      colSize: 3,
    },
    CondominiosComponent,
    FornecedoresAvancadoComponent,
  ];

  // --------- request do relatório ----------
  const onRequestReport = async (data: any) => {
    try {
      setLoading(true);

      const despesa = data.despesas
        ? FILTRAR_POR_DESPESAS.find((i) => i.value === data.despesas)?.field
        : data.despesas;

      const dtInicio = moment(data.dt_inicio, "YYYY-MM-DD").format(
        "MM/DD/YYYY"
      );
      const dtFim = moment(data.dt_fim, "YYYY-MM-DD").format("MM/DD/YYYY");

      const status = data.status
        ? STATUS_DESPESAS.find((i) => i.value === data.status)?.label
        : data.status;

      const query =
        `?despesas=${despesa}&` +
        `dt_inicio=${dtInicio}&` +
        `dt_fim=${dtFim}&` +
        `status=${encodeURIComponent(status ?? "")}&` +
        `id_condominios=${(data.id_condominios || []).toString()}&` +
        `id_fornecedores=${(data.id_fornecedores || []).toString()}`;

      const result = await new ApiReportSOS().compras(query);
      if (result.success) {
        setResults(result.data || []);
      }
      if (result.message) setMessages(result.message);
    } finally {
      setLoading(false);
    }
  };

  // --------- tabela (placeholder de colunas) ----------
  const columnsTable: ITableDataColumns[] = useMemo(
    () => [
      {
        columnKey: "dt_despesa_des",
        label: "Data Despesa",
        alignText: "text-center",
        render: (item: ISosCompra) =>
          item.dt_despesa_des
            ? moment(item.dt_despesa_des, "MM/DD/YYYY").format("DD/MM/YYYY")
            : "",
        sortValue: (item: ISosCompra) =>
          moment(item.dt_despesa_des, "MM/DD/YYYY"),
      },
      {
        columnKey: "dt_liquidacao_pdes",
        label: "Data Liquidação",
        alignText: "text-center",
        render: (item: ISosCompra) =>
          item.dt_liquidacao_pdes
            ? moment(item.dt_liquidacao_pdes, "MM/DD/YYYY").format("DD/MM/YYYY")
            : "",
        sortValue: (item: ISosCompra) =>
          moment(item.dt_liquidacao_pdes, "MM/DD/YYYY"),
      },
      {
        columnKey: "st_documento_des",
        label: "Documento",
        alignText: "text-center",
      },
      {
        columnKey: "st_fantasia_cond",
        label: "Condomínio",
        alignText: "text-start",
      },
      {
        columnKey: "st_nome_con",
        label: "Fornecedor",
        alignText: "text-start",
      },
      {
        columnKey: "vl_valorbruto_pdes",
        label: "Valor Bruto",
        alignText: "text-end",
        render: (item: ISosCompra) =>
          numeral(
            (item.vl_valorbruto_pdes ?? 0).toString().replace?.(".", ",") ??
              item.vl_valorbruto_pdes
          ).format("#,##0.00"),
        sortValue: (item: ISosCompra) => {
          const v =
            typeof item.vl_valorbruto_pdes === "string"
              ? parseFloat(item.vl_valorbruto_pdes)
              : item.vl_valorbruto_pdes || 0;
          return v;
        },
      },
      {
        columnKey: "vl_valor_pdes",
        label: "Valor Líquido",
        alignText: "text-end",
        render: (item: ISosCompra) =>
          numeral(
            (item.vl_valor_pdes ?? 0).toString().replace?.(".", ",") ??
              item.vl_valor_pdes
          ).format("#,##0.00"),
        sortValue: (item: ISosCompra) => {
          const v =
            typeof item.vl_valor_pdes === "string"
              ? parseFloat(item.vl_valor_pdes)
              : item.vl_valor_pdes || 0;
          return v;
        },
      },
    ],
    []
  );

  // --------- exports (mínimo viável) ----------
  const getExportDataPDF = (): IDataPDF => {
    const header = [
      { text: "Data Despesa", alignment: "center", bold: true },
      { text: "Data Líquidação", alignment: "center", bold: true },
      { text: "Documento", alignment: "center", bold: true },
      { text: "Condomínio", alignment: "center", bold: true },
      { text: "Fornecedor", alignment: "center", bold: true },
      { text: "Valor Bruto", alignment: "center", bold: true },
      { text: "Valor Líquido", alignment: "center", bold: true },
    ];

    const body = results.map((r) => [
      r.dt_despesa_des
        ? moment(r.dt_despesa_des, "MM/DD/YYYY").format("DD/MM/YYYY")
        : "",
      r.dt_liquidacao_pdes
        ? moment(r.dt_liquidacao_pdes, "MM/DD/YYYY").format("DD/MM/YYYY")
        : "",
      r.st_documento_des ?? "",
      r.st_fantasia_cond ?? "",
      r.st_nome_con ?? "",
      numeral(r.vl_valorbruto_pdes ?? 0).format("#,##0.00"),
      numeral(r.vl_valor_pdes ?? 0).format("#,##0.00"),
    ]);

    return {
      headerTitle: "Relatório de Compras (SOS)",
      pageOrientation: "landscape",
      content: {
        style: "table",
        table: {
          headerRows: 1,
          body: [header, ...body],
        },
      },
      styles: { table: { fontSize: 7 } },
    };
  };

  const getExportDataExcel = (): IDataExcel => {
    const columns: IExcelColumn[] = [
      {
        header: "Data Despesa",
        width: 12,
        style: { alignment: { horizontal: "center" }, numFmt: "DD/MM/YYYY" },
      },
      {
        header: "Data Líquida",
        width: 12,
        style: { alignment: { horizontal: "center" }, numFmt: "DD/MM/YYYY" },
      },
      { header: "Documento", width: 20 },
      { header: "Condomínio", width: 40 },
      { header: "Fornecedor", width: 40 },
      {
        header: "Valor Bruto",
        width: 14,
        style: { alignment: { horizontal: "right" }, numFmt: "#,##0.00" },
      },
      {
        header: "Valor Líquido",
        width: 14,
        style: { alignment: { horizontal: "right" }, numFmt: "#,##0.00" },
      },
    ];

    const rows = results.map((r) => [
      r.dt_despesa_des
        ? moment(r.dt_despesa_des, "MM/DD/YYYY").format("DD/MM/YYYY")
        : "",
      r.dt_liquidacao_pdes
        ? moment(r.dt_liquidacao_pdes, "MM/DD/YYYY").format("DD/MM/YYYY")
        : "",
      r.st_documento_des ?? "",
      r.st_fantasia_cond ?? "",
      r.st_nome_con ?? "",
      typeof r.vl_valorbruto_pdes === "string"
        ? parseFloat(r.vl_valorbruto_pdes)
        : (r.vl_valorbruto_pdes ?? 0),
      typeof r.vl_valor_pdes === "string"
        ? parseFloat(r.vl_valor_pdes)
        : (r.vl_valor_pdes ?? 0),
    ]);

    return {
      headerTitle: "Relatório de Compras (SOS)",
      sheetName: "Compras SOS",
      columns,
      rows,
      pageSetup: {
        orientation: "landscape",
        fitToPage: true,
        fitToHeight: 100,
        paperSize: 9,
        margins: {
          left: 0.7,
          right: 0.7,
          top: 0.75,
          bottom: 0.75,
          header: 0.3,
          footer: 0.3,
        },
      },
    };
  };

  // recebe do modal
  const onResultListaFornecedoresModal = async (
    item: ListaFornecedoresResult | null
  ) => {
    try {
      setLoading(true);
      if (!item) return;

      const ids = (item.itens ?? [])
        .map((i) => Number(i.id_fornecedor))
        .filter((n) => Number.isFinite(n));

      if (!item.id_lista) {
        // CREATE
        const payload = {
          nome_lista: item.nome_lista,
          observacao: item.observacao,
          ativo: true,
          itens: ids,
        };
        const result = await new ApiFornecedoresListas(payload).create();
        if (result.success) {
          if (result.message) addNotification(result.message[0]);

          // recarrega listas e pega a recém-criada
          const fresh = await loadFornecedorLists();
          const newId =
            result?.data?.id_lista ??
            fresh.find((l) => l.nome_lista === item.nome_lista)?.id_lista ??
            null;

          if (newId) {
            setSelectedListId(Number(newId));
          }

          // aplica ids (usa result.data como source se disponível)
          const source: IFornecedorList | undefined = result?.data;
          applyIdsToSelect(ids, source);
        } else if (result.message) {
          setMessages(result.message);
        }
      } else {
        // UPDATE (nome/obs)
        const result = await new ApiFornecedoresListas({
          data: { nome_lista: item.nome_lista, observacao: item.observacao },
        }).update(item.id_lista);

        if (result.success && result.message)
          addNotification(result.message[0]);

        // diff e add/remove
        const currentState = fornecedorLists.find(
          (l) => l.id_lista === item.id_lista
        );
        const atuais = new Set(
          (currentState?.itens ?? []).map((i) => i.id_fornecedor)
        );
        const futuros = new Set(ids);

        const paraAdicionar: number[] = [];
        ids.forEach((fid) => {
          if (!atuais.has(fid)) paraAdicionar.push(fid);
        });

        const paraRemover: Array<{ id_item: number }> = [];
        (currentState?.itens ?? []).forEach((it) => {
          if (!futuros.has(it.id_fornecedor))
            paraRemover.push({ id_item: it.id_item });
        });

        if (paraAdicionar.length) {
          await new ApiFornecedoresListas().addItens(
            item.id_lista!,
            paraAdicionar
          );
        }
        for (const r of paraRemover) {
          await new ApiFornecedoresListas().removeItem(
            item.id_lista!,
            r.id_item
          );
        }

        // recarrega e aplica
        const fresh = await loadFornecedorLists();
        const updatedList = fresh.find((l) => l.id_lista === item.id_lista);
        setSelectedListId(item.id_lista);
        applyIdsToSelect(ids, updatedList);
      }
    } finally {
      setShowListaFornecedoresModal(false);
      setSelectedFornecedorLista(null);
      setLoading(false);
    }
  };

  return (
    <DefaultBasePage
      title="Relatórios - Compras (SOS)"
      subtitle={`Resultados: ${results.length}`}
      h3
      loading={loading}
      message={messages}
    >
      <DefaultReportPage
        titleFilters="Filtros de Compras (SOS)"
        titleResults="Resultados"
        fields={fields}
        onRequestReport={onRequestReport}
        resultsData={results}
        columnsTable={columnsTable}
        defaultValues={{
          dt_inicio: prevMonthStart,
          dt_fim: prevMonthEnd,
          status: "1", // liquidados (segue o padrão do financeiro)
          despesas: "6", // vencimento (mesmo valor usado no financeiro)
          id_condominios: ["-1"],
          id_fornecedores: [],
        }}
        totalizerPosition="none"
        setLoading={setLoading}
        setMessagesExport={setMessages}
        getExportDataPDF={getExportDataPDF}
        getExportDataExcel={getExportDataExcel}
        exportButtonTitle="Exportar"
        reportName="Compras (SOS)"
      />

      <ListaFornecedoresModal
        showModal={showListaFornecedoresModal}
        data={selectedFornecedorLista}
        onResult={onResultListaFornecedoresModal}
      />
    </DefaultBasePage>
  );
};

export default RelatorioSOSComprasPrivatePage;
